import { RowLayout } from '../../../Vendors/VendorCreation/General/VendorForm/RowLayout'
import { useHSDetailsContext } from '../Providers/HSContextProvider'
import { LABELS } from '../../../../../constants'
import { FieldWithLabel } from '../../../../../components/FieldWithLabel/FieldWithLabel'
import { Typography } from '../../../../../components/Typography'
import {
  formatAddress,
  formatPhoneNumber,
  getUserCommunity
} from '../../../../../helper/common'
import { LinkHighlighter } from '../../../../../components/LinkHighlighter/LinkHighlighter'
import { useSelector } from 'react-redux'
import { getUser } from '../../../../../redux/store/user/getters'
import {
  ability,
  ACTIONS,
  Can,
  SUBJECTS,
  SubjectWithAttributes
} from 'features/Permission'
import { subject } from '@casl/ability'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getDefaultDepartmentsListRequest } from '../../../api'
import cn from 'classnames'
import { QuickNavigationBlock } from '../../../MyOrganizationCommunity/QuickNavigationBlock'

export const ViewHS = () => {
  const {
    state: { details, outsideCommunityMode }
  } = useHSDetailsContext()
  const user = useSelector(getUser)

  const [departments, setDepartments] = useState<string[]>([])

  const canSeeDepartments = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        subject(SUBJECTS.DEPARTMENTS_IN_HS_DETAILS, {
          ...details
        } as unknown as SubjectWithAttributes)
      ),
    [details]
  )

  const canSeeLegalName = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        subject(SUBJECTS.HEALTH_SYSTEM_LEGAL_NAME, {
          ...details
        } as unknown as SubjectWithAttributes)
      ),
    [details]
  )

  const setDepartmentsList = useCallback(async () => {
    const defaultDepartments = await getDefaultDepartmentsListRequest(
      getUserCommunity(user) ?? ''
    )
    setDepartments([
      ...defaultDepartments.data.results.map((department) => department.name),
      ...(details?.added_departments?.map((department) => department.name) ??
        [])
    ])
  }, [user, details])

  useEffect(() => {
    if (canSeeDepartments && details) {
      setDepartmentsList()
    }
  }, [canSeeDepartments, details, setDepartmentsList])

  if (!details) return null

  return (
    <div className="row gap-24">
      <div>
        <RowLayout title="Business information">
          <div className={cn('row mt-12', { 'two-columns': canSeeLegalName })}>
            <FieldWithLabel
              className={cn({ 'half-width': canSeeLegalName })}
              title={
                canSeeLegalName
                  ? LABELS.HS_NAME_DOING_BUSINESS_AS
                  : LABELS.HEALTH_SYSTEM_NAME
              }
            >
              {details.name}
            </FieldWithLabel>
            {canSeeLegalName && (
              <FieldWithLabel title={LABELS.HS_LEGAL_NAME}>
                {details.legal_name ?? '-'}
              </FieldWithLabel>
            )}
          </div>
          <div className="row two-columns">
            <FieldWithLabel className="half-width" title={LABELS.WEBSITE}>
              {details.website ? (
                <LinkHighlighter text={details.website} />
              ) : (
                '-'
              )}
            </FieldWithLabel>
            <FieldWithLabel title={LABELS.PHONE_NUMBER}>
              {formatPhoneNumber(details.phone_number)}
            </FieldWithLabel>
          </div>
        </RowLayout>

        <div className="vendor-details-form__divider" />
        <RowLayout title="Address">
          <Typography.Body1>{formatAddress(details.address)}</Typography.Body1>
        </RowLayout>
        {canSeeDepartments && departments.length && (
          <>
            <div className="vendor-details-form__divider" />
            <RowLayout title="Departments">
              <Typography.Body1>{departments.join(', ')}</Typography.Body1>
            </RowLayout>
          </>
        )}
      </div>
      <Can
        I={ACTIONS.VIEW}
        a={subject(SUBJECTS.QUICK_NAVIGATION_BLOCK_HS, {
          ...user
        } as unknown as SubjectWithAttributes)}
      >
        <QuickNavigationBlock outsideCommunityMode={outsideCommunityMode} />
      </Can>
    </div>
  )
}
